import React from 'react';
import {useLocation} from 'react-router';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import {useExperimentsStore} from 'apps/experiments/src/store';
import {ReportName} from 'common/report/constants';
import {handleFetchBookmarkURLError} from 'common/report/util/bookmark-urls';
import {bookmarkUrlsClient} from 'common/report/queries/app-api/bookmark-urls-client';
import {navigate} from 'common/util/url';
import {ExperimentUpdates} from 'iron/apps/experiments/src/types';

import {DRAFT_KEYS} from './staged-experiment';

const DEBOUNCE = 1000;

export function useExperimentBookmarkSync({projectId, experimentId}: {projectId: number; experimentId: string}) {
  const {hash} = useLocation();
  const draftExperiment = useExperimentsStore((state) => state.draftExperiment);
  const setDraftExperiment = useExperimentsStore((state) => state.setDraftExperiment);

  React.useEffect(() => {
    const urlHash = hash.slice(1);

    if (urlHash) {
      const requestParams = {
        projectId,
        slug: urlHash,
      };
      try {
        bookmarkUrlsClient.fetchBookmarkUrl(requestParams).then((response) => {
          if (response) {
            const params = response.params as Obj;
            if (!isEmpty(params)) {
              setDraftExperiment({
                settings: params?.settings ?? {},
                metrics: params?.metrics,
                start_date: params?.start_date,
                end_date: params?.end_date,
              });
            }
          }
        });
      } catch (err) {
        handleFetchBookmarkURLError(err, {...requestParams, reportName: ReportName.Experiments});
      }
    }
  }, []);

  const debouncedSyncBookmark = React.useMemo(
    () =>
      debounce((_draftExperiment: ExperimentUpdates) => {
        const params: Obj = {
          id: experimentId,
        };
        if (_draftExperiment) {
          DRAFT_KEYS.forEach((key) => {
            if (_draftExperiment[key] !== undefined) {
              params[key] = _draftExperiment[key];
            }
          });
        }
        const slug = bookmarkUrlsClient.getBookmarkUrl({
          project_id: projectId,
          type: ReportName.Experiments,
          params,
        });
        navigate({hash: slug}, {replace: true});
      }, DEBOUNCE),
    [projectId, experimentId],
  );

  React.useEffect(() => {
    if (!draftExperiment) {
      navigate({hash: undefined}, {replace: true});
      return;
    }
    debouncedSyncBookmark(draftExperiment);
  }, [projectId, experimentId, draftExperiment, debouncedSyncBookmark]);
}
